import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    HierarchicalTableData,
    PendingChange,
    ProductionCapabilityData,
    HandlingTierData,
    ActionType,
} from './types';
import { HierarchicalTable } from './HierarchicalTable';
import { GSLADefinitionsByMutipleWarehouses, GSLARecommendationsByMutipleWarehouses, GSLAPublishRequest } from 'src/features/types/gslaApi';
import { publishGSLAValues, deleteGSLA } from 'src/utils/Queries';
import { WarehouseHandlingTier } from 'src/types';
import { useUser } from 'src/utils/UserContext';
import { AddProductionCapability } from '../common/AddProductionCapability';
import { useDefaultPCSafety } from '../../hooks/useDefaultPCSafety';
import SafetyWarning from '../common/SafetyWarning';

// Custom event for PC addition
export const PC_ADDED_EVENT = 'pc-added-event';

interface GSLAHierarchicalTableAdapterProps
{
    warehouseId: string;
    gslaData?: GSLADefinitionsByMutipleWarehouses;
    gslaRecommendations?: GSLARecommendationsByMutipleWarehouses;
    isLoading: boolean;
    userCanEdit: boolean;
    onSaveSuccess?: (message?: string) => void;
}

/**
 * Adapter component that converts API data to the format expected by the HierarchicalTable
 * and handles saving changes back to the API
 */
export const GSLAHierarchicalTableAdapter: React.FC<GSLAHierarchicalTableAdapterProps> = ( {
    warehouseId,
    gslaData,
    gslaRecommendations,
    isLoading,
    userCanEdit,
    onSaveSuccess,
} ) =>
{
    const { user } = useUser();
    // Transform the API data into the format expected by HierarchicalTable
    const hierarchicalData: HierarchicalTableData | undefined = useMemo( () =>
    {
        if ( !gslaData || !warehouseId ) return undefined;

        const productionCapabilities: ProductionCapabilityData[] = [];

        // Group the GSLA definitions by production capability
        const pcMap = new Map<string, {
            gslaDefinitions: {
                handlingTier: string;
                gslaInSeconds: number;
                publishDateEpochMillis: number;
            }[];
            recommendations?: {
                gslaInSeconds: number;
                publishDateEpochMillis: number;
            };
        }>();

        // Process the GSLA data (includes handling tier info)
        // Check both the specific warehouse and the DEFAULT warehouse for default PCs
        const warehouseGSLAs = gslaData.warehouseToGSLAMap[ warehouseId ] || [];
        const defaultWarehouseGSLAs = gslaData.warehouseToGSLAMap[ 'DEFAULT' ] || [];
        
        // Combine GSLAs from both warehouses
        const allGSLAs = [...warehouseGSLAs, ...defaultWarehouseGSLAs];
        
        allGSLAs.forEach( gsla =>
        {
            const pcId = gsla.productionCapability;
            const handlingTier = gsla.warehouseHandlingTier || WarehouseHandlingTier.Default;

            let pcEntry = pcMap.get( pcId );
            if ( !pcEntry )
            {
                pcEntry = { gslaDefinitions: [] };
                pcMap.set( pcId, pcEntry );
            }

            pcEntry.gslaDefinitions.push( {
                handlingTier,
                gslaInSeconds: gsla.gslaInSeconds,
                publishDateEpochMillis: gsla.publishDateEpochMillis,
            } );
        } );

        // Process the recommendation data (at PC level, no handling tier)
        // Create recommendations for all PCs, even ones without GSLA data
        // Check both the specific warehouse and the DEFAULT warehouse for recommendations
        const warehouseRecommendations = gslaRecommendations?.warehouseToGSLARecommendationMap[ warehouseId ] || [];
        const defaultWarehouseRecommendations = gslaRecommendations?.warehouseToGSLARecommendationMap[ 'DEFAULT' ] || [];
        
        // Combine recommendations from both warehouses
        const allRecommendations = [...warehouseRecommendations, ...defaultWarehouseRecommendations];
        
        allRecommendations.forEach( rec =>
        {
            const pcId = rec.productionCapability;

            let pcEntry = pcMap.get( pcId );
            if ( !pcEntry )
            {
                pcEntry = { gslaDefinitions: [] };
                pcMap.set( pcId, pcEntry );
            }

            pcEntry.recommendations = {
                gslaInSeconds: rec.gslaInSeconds,
                publishDateEpochMillis: rec.publishDateEpochMillis,
            };
        } );

        // Convert the map to the hierarchical data structure
        pcMap.forEach( ( pcEntry, pcId ) =>
        {
            const defaultTier = pcEntry.gslaDefinitions.find( def =>
                def.handlingTier === WarehouseHandlingTier.Default || !def.handlingTier );

            const additionalTiers = pcEntry.gslaDefinitions.filter( def =>
                def.handlingTier && def.handlingTier !== WarehouseHandlingTier.Default );

            // Only create a PC entry if there's either:
            // 1. A default tier with actual GSLA data, OR
            // 2. At least one non-default tier with actual GSLA data, OR
            // 3. There are recommendations (even without current GSLA data)
            const hasDefaultTierData = defaultTier?.gslaInSeconds !== undefined;
            const hasAdditionalTierData = additionalTiers.length > 0;
            const hasRecommendations = pcEntry.recommendations?.gslaInSeconds !== undefined;
            
            // Skip this PC if it has no actual data and no recommendations
            if (!hasDefaultTierData && !hasAdditionalTierData && !hasRecommendations) {
                return;
            }

            const pc: ProductionCapabilityData = {
                id: pcId,
                name: pcId,
                defaultHandlingTier: {
                    id: `${pcId}_default`,
                    name: WarehouseHandlingTier.Default,
                    currentGSLA: defaultTier?.gslaInSeconds,
                    recommendedGSLA: pcEntry.recommendations?.gslaInSeconds,
                    publishDateEpochMillis: defaultTier?.publishDateEpochMillis,
                    recommendationDateEpochMillis: pcEntry.recommendations?.publishDateEpochMillis,
                    isDefault: true,
                },
                additionalHandlingTiers: additionalTiers.map( tier => (
                    {
                        id: `${pcId}_${tier.handlingTier.toLowerCase().replace( /\s+/g, '_' )}`,
                        name: tier.handlingTier,
                        currentGSLA: tier.gslaInSeconds,
                        recommendedGSLA: undefined, // Recommendations are at PC level, not tier level
                        publishDateEpochMillis: tier.publishDateEpochMillis,
                        recommendationDateEpochMillis: undefined,
                        isDefault: false,
                    }
                ) ),
            };

            productionCapabilities.push( pc );
        } );

        // Sort production capabilities alphabetically
        productionCapabilities.sort( ( a, b ) => a.name.localeCompare( b.name ) );

        return {
            warehouseId,
            productionCapabilities,
        };
    }, [ warehouseId, gslaData, gslaRecommendations ] );

    // Store hierarchical data in state so we can update it locally
    const [ localData, setLocalData ] = useState<HierarchicalTableData | undefined>( hierarchicalData );

    // State for pending changes to GSLA values
    const [ pendingChanges, setPendingChanges ] = useState<Record<string, Record<string, PendingChange>>>( {} );
    
    // Initialize safety hook for Default_PC protection
    const safety = useDefaultPCSafety({
        operationType: 'GSLA',
        userAlias: user?.login,
        warehouseId: warehouseId
    });

    // Update local data when hierarchical data changes from props
    useEffect( () =>
    {
        setLocalData( hierarchicalData );
    }, [ hierarchicalData ] );

    // Listen for PC_ADDED_EVENT to handle newly added PCs
    useEffect( () =>
    {
        const handlePCAdded = ( event: CustomEvent ) =>
        {
            const { pcName, tierName } = event.detail;

            // Use a unique suffix for the default tier ID to prevent React key collisions
            const uniqueSuffix = `_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
            const defaultTierId = `${pcName}_default${uniqueSuffix}`;

            // Add the new PC to local data
            setLocalData( prevData =>
            {
                if ( !prevData ) return prevData;

                // Create a new copy of the data
                const newData = { ...prevData };

                // Check if this PC already exists
                if ( newData.productionCapabilities.some( pc => pc.name === pcName ) )
                {
                    return prevData; // PC already exists, no need to update
                }

                // Create a new PC with a default tier
                const newPC: ProductionCapabilityData = {
                    id: pcName,
                    name: pcName,
                    defaultHandlingTier: {
                        id: defaultTierId,
                        name: WarehouseHandlingTier.Default,
                        currentGSLA: undefined, // New PCs don't have a current GSLA value
                        recommendedGSLA: undefined,
                        publishDateEpochMillis: undefined,
                        recommendationDateEpochMillis: undefined,
                        isDefault: true,
                    },
                    additionalHandlingTiers: [],
                };

                // Add the new PC to the array
                newData.productionCapabilities = [ ...newData.productionCapabilities, newPC ];

                // Sort production capabilities alphabetically
                newData.productionCapabilities.sort( ( a, b ) => a.name.localeCompare( b.name ) );

                return newData;
            } );

            // Add the appropriate tier as a pending change (this is the key step!)
            setPendingChanges( ( prev: Record<string, Record<string, PendingChange>> ) =>
            {
                const newChanges = { ...prev };

                if ( !newChanges[ pcName ] )
                {
                    newChanges[ pcName ] = {};
                }

                // Only create a pending change for the actually selected tier
                if ( tierName === WarehouseHandlingTier.Default )
                {
                    // User selected default tier - create default tier pending change
                    newChanges[ pcName ][ defaultTierId ] = {
                        productionCapability: pcName,
                        handlingTier: WarehouseHandlingTier.Default,
                        currentGSLA: undefined,
                        newGSLA: undefined, // No default value - user should enter their own
                        action: 'NewPC',
                        isDefault: true,
                    };
                }
                else
                {
                    // User selected non-default tier - create additional tier pending change
                    const nonDefaultTierId = `${pcName}_${tierName.toLowerCase().replace( /\s+/g, '_' )}`;
                    newChanges[ pcName ][ nonDefaultTierId ] = {
                        productionCapability: pcName,
                        handlingTier: tierName,
                        currentGSLA: undefined,
                        newGSLA: undefined, // No default value - user should enter their own
                        action: 'NewPC',
                        isDefault: false,
                    };

                    // Also update the local data to include this additional tier
                    setLocalData( prevData =>
                    {
                        if ( !prevData ) return prevData;

                        const updatedData = { ...prevData };
                        const pc = updatedData.productionCapabilities.find( p => p.name === pcName );

                        if ( pc )
                        {
                            const newTier: HandlingTierData = {
                                id: nonDefaultTierId,
                                name: tierName,
                                currentGSLA: undefined,
                                recommendedGSLA: undefined,
                                publishDateEpochMillis: undefined,
                                recommendationDateEpochMillis: undefined,
                                isDefault: false,
                            };

                            pc.additionalHandlingTiers = [ ...pc.additionalHandlingTiers, newTier ];
                        }

                        return updatedData;
                    } );
                }

                return newChanges;
            } );
        };

        // Add event listener for PC_ADDED_EVENT
        window.addEventListener( PC_ADDED_EVENT, handlePCAdded as EventListener );

        // Clean up
        return () =>
        {
            window.removeEventListener( PC_ADDED_EVENT, handlePCAdded as EventListener );
        };
    }, [] );

    // Apply local changes to the hierarchical data without refetching from the API
    const updateDataWithChanges = useCallback( ( changes: PendingChange[], timestamp: number ) =>
    {
        if ( !localData ) return;

        const updatedData = { ...localData };

        // Track PCs that need to be removed completely
        const pcsToDelete = new Set<string>();

        // First pass - identify PCs to delete
        changes.forEach( change =>
        {
            if ( change.action === ActionType.DELETE && change.isDefault )
            {
                pcsToDelete.add( change.productionCapability );
            }
        } );

        // Process each change
        changes.forEach( change =>
        {
            // Find the PC that this change belongs to
            const pc = updatedData.productionCapabilities.find( ( p: ProductionCapabilityData ) => p.name === change.productionCapability );
            if ( !pc ) return;

            // Skip processing additional tier changes for PCs that are being deleted entirely
            if ( pcsToDelete.has( change.productionCapability ) && !change.isDefault )
            {
                return;
            }

            if ( change.action === ActionType.DELETE )
            {
                if ( change.isDefault )
                {
                    // If deleting the default tier (entire PC), mark this PC for removal
                }
                else
                {
                    pc.additionalHandlingTiers = pc.additionalHandlingTiers.filter(
                        ( t: HandlingTierData ) => t.name !== change.handlingTier,
                    );
                }
                return;
            }

            // Handle update/create case
            if ( change.isDefault )
            {
                // Update default tier
                pc.defaultHandlingTier = {
                    ...pc.defaultHandlingTier,
                    currentGSLA: change.newGSLA,
                    publishDateEpochMillis: timestamp,
                };
            }
            else
            {
                // Find additional tier
                const tierIndex = pc.additionalHandlingTiers.findIndex( ( t: HandlingTierData ) => t.name === change.handlingTier );

                if ( tierIndex >= 0 )
                {
                    // Update existing tier
                    pc.additionalHandlingTiers[ tierIndex ] = {
                        ...pc.additionalHandlingTiers[ tierIndex ],
                        currentGSLA: change.newGSLA,
                        publishDateEpochMillis: timestamp,
                    };
                }
                else
                {
                    // This is a new handling tier that was just added - create and add it
                    // Use a unique ID format to prevent React key collisions
                    const uniqueSuffix = `_${Date.now()}_${Math.floor( Math.random() * 1000 )}`;
                    const tierId = `${pc.id}_${change.handlingTier.toLowerCase().replace( /\s+/g, '_' )}${uniqueSuffix}`;
                    const newTier: HandlingTierData = {
                        id: tierId,
                        name: change.handlingTier,
                        currentGSLA: change.newGSLA,
                        recommendedGSLA: undefined,
                        publishDateEpochMillis: timestamp,
                        recommendationDateEpochMillis: undefined,
                        isDefault: false,
                    };

                    // Add the new tier to the PC - but first check if a tier with this name already exists
                    const existingTier = pc.additionalHandlingTiers.find( t => t.name === change.handlingTier );
                    if ( !existingTier )
                    {
                        pc.additionalHandlingTiers.push( newTier );
                    }
                    else
                    {
                        const index = pc.additionalHandlingTiers.findIndex( t => t.name === change.handlingTier );
                        if ( index >= 0 )
                        {
                            pc.additionalHandlingTiers[ index ].currentGSLA = change.newGSLA;
                            pc.additionalHandlingTiers[ index ].publishDateEpochMillis = timestamp;
                        }
                    }
                }
            }
        } );

        // Process PCs that were marked for deletion
        // Instead of removing them, set their currentGSLA to undefined (N/A)
        if ( pcsToDelete.size > 0 )
        {
            // Find each PC that was deleted and update its state
            updatedData.productionCapabilities.forEach( pc =>
            {
                if ( pcsToDelete.has( pc.name ) )
                {
                    // Update the default tier to have undefined currentGSLA (will show as N/A)
                    pc.defaultHandlingTier = {
                        ...pc.defaultHandlingTier,
                        currentGSLA: undefined,  // Set to undefined which will display as N/A
                        publishDateEpochMillis: undefined, // Clear the publish date
                    };

                    // Remove all additional handling tiers when the PC is deleted
                    // since there are no recommendations for individual tiers
                    pc.additionalHandlingTiers = [];
                }
            } );
        }

        setLocalData( updatedData );
    }, [ localData ] );

    // Handle saving changes
    const handleSave = useCallback( async ( changes: PendingChange[] ) =>
    {
        if ( !warehouseId || changes.length === 0 )
        {
            return;
        }
        
        // CRITICAL SAFETY CHECK: Validate for Default_PC entries
        const safetyCheck = safety.validateSafety(changes);
        
        if (safetyCheck.shouldBlock) {
            // Block the save operation and show error message
            safety.showWarning(safetyCheck.errorMessage, 'error');
            
            // Log the safety violation for audit purposes
            safety.logViolation(safetyCheck.defaultPCCount);
            
            // Call onSaveSuccess with error message to trigger parent notification
            if (onSaveSuccess) {
                onSaveSuccess(safetyCheck.userMessage);
            }
            
            // Do not proceed with the save
            return;
        }

        const publishTimestamp = Date.now();

        const updatesToPublish: PendingChange[] = [];
        const deletionsToProcess: PendingChange[] = [];

        for ( const change of changes )
        {
            if ( change.action === ActionType.DELETE )
            {
                deletionsToProcess.push( change );
            }
            else
            {
                updatesToPublish.push( change );
            }
        }

        try
        {
            if ( updatesToPublish.length > 0 )
            {
                // Filter out changes that don't have a valid newGSLA value
                const validUpdates = updatesToPublish.filter( change =>
                    change.newGSLA !== undefined && change.newGSLA > 0
                );
                
                if ( validUpdates.length === 0 ) {
                    // No valid updates to publish
                    if ( onSaveSuccess ) {
                        onSaveSuccess();
                    }
                    return;
                }
                
                const gslaToPublishList = validUpdates.map( change =>
                {
                    let handlingTierValue: string;
                    if ( change.isDefault )
                    {
                        handlingTierValue = WarehouseHandlingTier.Default;
                    }
                    else if ( change.handlingTier === WarehouseHandlingTier.expedited )
                    {
                        handlingTierValue = WarehouseHandlingTier.expedited;
                    }
                    else if ( change.handlingTier === WarehouseHandlingTier.pa_expedited )
                    {
                        handlingTierValue = WarehouseHandlingTier.pa_expedited;
                    }
                    else if ( change.handlingTier === WarehouseHandlingTier.rapid )
                    {
                        handlingTierValue = WarehouseHandlingTier.rapid;
                    }
                    else if ( change.handlingTier === WarehouseHandlingTier.rush )
                    {
                        handlingTierValue = WarehouseHandlingTier.rush;
                    }
                    else
                    {
                        handlingTierValue = change.handlingTier;
                    }

                    return {
                        warehouseId,
                        productionCapability: change.productionCapability,
                        gslaInSeconds: change.newGSLA!, // Safe to assert since we filtered for valid values
                        userAlias: user?.login || 'unknown',
                        publishDateEpochMillis: publishTimestamp,
                        actionSource: 'website',
                        reason: change.action,
                        warehouseHandlingTier: handlingTierValue,
                    };
                } );

                await publishGSLAValues( {
                    gslaToPublishList,
                    preserveDefaultTypes: true,
                } );
            }

            // Process deletions if any exist
            if ( deletionsToProcess.length > 0 )
            {
                for ( const deletion of deletionsToProcess )
                {
                    // When deleting a default tier (entire PC), we need to pass undefined for warehouseHandlingTier
                    // This tells the backend to delete the entire PC instead of a specific tier
                    await deleteGSLA( {
                        warehouseId,
                        productionCapability: deletion.productionCapability,
                        warehouseHandlingTier: deletion.isDefault ? undefined : deletion.handlingTier,
                        userAlias: user?.login || 'unknown',
                        actionSource: 'website',
                        reason: 'Deleted via UI',
                    } );
                }
            }

            // Create a new local copy to trigger a proper re-render
            if ( localData )
            {
                // For PC deletions, we need to force a complete re-render with the updated state
                const updatedLocalData = {
                    ...localData,
                    productionCapabilities: [ ...localData.productionCapabilities ],
                };
                const changesCopy = changes.map( change => (
                    { ...change }
                ) );

                updateDataWithChanges( changesCopy, publishTimestamp );
            }

            if ( onSaveSuccess )
            {
                onSaveSuccess();
            }
        }
        catch ( error )
        {
            console.error( 'Error saving GSLA changes:', error );
        }
    }, [ warehouseId, user, onSaveSuccess, updateDataWithChanges ] );

    // Show loading state
    if ( isLoading )
    {
        return <div>Loading GSLA data...</div>;
    }

    // Show message if no data
    if ( !localData )
    {
        return <div>No GSLA data available for this warehouse.</div>;
    }

    return (
        <>
            {/* Safety Warning Display */}
            {safety.warningState?.isVisible && (
                <SafetyWarning
                    message={safety.warningState.message}
                    type={safety.warningState.type}
                    operationType={safety.warningState.operationType}
                    onDismiss={safety.dismissWarning}
                />
            )}
            
            {/* Add Production Capability Button */}
            {userCanEdit && (
                <AddProductionCapability
                    existingPCs={localData.productionCapabilities.map(pc => pc.name)}
                    onAdd={async (pcName: string, warehouseHandlingTier: string) => {
                        // The PC_ADDED_EVENT will be dispatched by the component itself
                        // We just need to return true to indicate success
                        return true;
                    }}
                    userCanEdit={userCanEdit}
                    
                    // New API integration props
                    warehouseIds={[warehouseId]}
                    enableAPIMode={true}
                    fallbackToTextInput={true}
                    showErrorDetails={false}
                    
                    // UI customization
                    dropdownPlaceholder="Select a Production Capability for this warehouse"
                    textInputPlaceholder="Enter Production Capability name"
                    loadingMessage="Loading production capabilities..."
                    
                    // Default_Network_PC restriction support
                    warehouseId={warehouseId}
                    
                    // Event callbacks for better user experience
                    onAPIError={(error) => {
                        console.warn('Failed to load production capabilities from API:', error.message);
                    }}
                    onModeSwitch={(mode) => {
                        console.log(`AddProductionCapability switched to ${mode} mode`);
                    }}
                />
            )}
            
            <HierarchicalTable
                data={localData}
                onSave={handleSave}
                userCanEdit={userCanEdit}
                initialPendingChanges={pendingChanges}
                warehouseId={warehouseId}
            />
        </>
    );
};
